import {useSorting} from '@hconnect/common/hooks/useSorting'
import {Column, customThemeConstants, DataTable, PaginationOptions} from '@hconnect/uikit/src/lib2'
import {Box, SortDirection, SxProps, Theme} from '@mui/material'
import {get, orderBy} from 'lodash'
import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {Instruction} from '../../types/documents.types'

type DocumentTableColumnKey = 'name' | 'processStage' | 'status'

const rowSx = (selected: boolean): SxProps<Theme> => [
  selected &&
    ((theme) => ({
      backgroundColor: theme.palette.grey[200]
    })),
  {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: customThemeConstants().palette.primaryBackgroundColor
    },
    td: {
      fontSize: 16,
      lineHeight: 3
    }
  }
]

const sortDocuments = (
  documents: Instruction[],
  orderByKey: DocumentTableColumnKey,
  sortDir: SortDirection
): Instruction[] =>
  orderBy(
    documents,
    [
      (document) => {
        return get(document, orderByKey, '').toLowerCase()
      }
    ],
    [sortDir]
  )

type DocumentsTableProps = {
  isLoading: boolean
  columns: Column<Instruction>[]
  selectedDocumentId?: string
  onRowClick(item: Instruction, i: number): void
  documents?: Instruction[]
  paginationOptions?: PaginationOptions
}

export const DocumentsTable: FC<DocumentsTableProps> = ({
  isLoading,
  documents,
  paginationOptions,
  columns,
  selectedDocumentId,
  onRowClick
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {sortedList, onSortClick, orderByKey, sortDir} = useSorting<
    Instruction,
    DocumentTableColumnKey
  >({
    initialSortDir: false,
    data: documents || [],
    sort: sortDocuments
  })
  const sortDirection = !sortDir ? undefined : sortDir

  return (
    <Box
      data-test-id="documents-table"
      display="flex"
      flexDirection="column"
      flex={1}
      overflow="hidden"
      justifyContent="space-between"
    >
      <DataTable<Instruction>
        columns={columns}
        data={sortedList || []}
        onSort={(event, key: DocumentTableColumnKey) => onSortClick(key)}
        sortedBy={orderByKey}
        sortDirection={sortDirection}
        emptyMessage={(!isLoading && t('documents.label.noData')) ?? ''}
        paginationOptions={paginationOptions}
        rowSx={(document) => rowSx(selectedDocumentId === document.id)}
        onRowClick={(keyEvent: React.MouseEvent, clickedItem: Instruction, i: number) =>
          onRowClick(clickedItem, i)
        }
      />
    </Box>
  )
}
